import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Search } from 'lucide-react';

const ManageFlights = () => {
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchFlights();
  }, []);

  const fetchFlights = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/flights`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFlights(data);
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (flightId, flightNumber) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer le vol ${flightNumber} ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/flights/${flightId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        alert('Vol supprimé avec succès');
        fetchFlights();
      } else {
        alert('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const filteredFlights = flights.filter(flight =>
    flight.flight_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (flight.title && flight.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
    flight.departure.toLowerCase().includes(searchTerm.toLowerCase()) ||
    flight.arrival.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    switch (status) {
      case 'Planifié': return '#2c5f7d';
      case 'En cours': return '#8b4f6d';
      case 'Terminé': return '#999';
      case 'Annulé': return '#c41e3a';
      default: return '#666';
    }
  };

  return (
    <AdminLayout>
      <div style={{ padding: '32px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '32px'
        }}>
          <h1 className="hero-medium">Gestion des Vols</h1>
          <button
            onClick={() => navigate('/admin/flights/add')}
            className="btn-primary"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <Plus size={20} />
            Ajouter un vol
          </button>
        </div>

        {/* Search Bar */}
        <div style={{ marginBottom: '24px', position: 'relative' }}>
          <Search size={20} style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'var(--text-secondary)'
          }} />
          <input
            type="text"
            placeholder="Rechercher un vol (numéro, titre, départ, arrivée)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 40px',
              border: '1px solid var(--border-light)',
              borderRadius: '0px',
              fontSize: '14px',
              fontFamily: 'inherit',
              background: 'var(--bg-primary)'
            }}
          />
        </div>

        {loading ? (
          <p>Chargement...</p>
        ) : filteredFlights.length === 0 ? (
          <div style={{
            padding: '48px',
            textAlign: 'center',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)'
          }}>
            <p className="body-regular" style={{ color: 'var(--text-secondary)' }}>
              {searchTerm ? 'Aucun vol ne correspond à votre recherche' : 'Aucun vol enregistré'}
            </p>
          </div>
        ) : (
          <div style={{
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-light)',
            overflowX: 'auto'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse'
            }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Vol</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Titre</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Date</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Trajet</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Appareil</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Places</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Prix client</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'left',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Statut</th>
                  <th className="body-regular" style={{
                    padding: '16px',
                    textAlign: 'right',
                    fontWeight: 600,
                    color: 'var(--text-secondary)'
                  }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFlights.map((flight) => (
                  <tr key={flight.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
                    <td className="body-regular" style={{ padding: '16px', fontWeight: 600 }}>
                      {flight.flight_number}
                    </td>
                    <td className="body-small" style={{ padding: '16px', color: '#2c5f7d', fontWeight: 500 }}>
                      {flight.title || '—'}
                    </td>
                    <td className="body-small" style={{ padding: '16px', color: 'var(--text-secondary)' }}>
                      {flight.date}
                    </td>
                    <td className="body-small" style={{ padding: '16px' }}>
                      {flight.departure} → {flight.arrival}
                    </td>
                    <td className="body-small" style={{ padding: '16px', color: 'var(--text-secondary)' }}>
                      {flight.aircraft}
                    </td>
                    <td className="body-small" style={{ padding: '16px' }}>
                      {flight.seats_sold} / {flight.total_seats}
                    </td>
                    <td className="body-small" style={{ padding: '16px', fontWeight: 600 }}>
                      {flight.client_price.toLocaleString('fr-FR')} €
                    </td>
                    <td className="body-small" style={{ padding: '16px' }}>
                      <span style={{
                        padding: '4px 12px',
                        background: getStatusColor(flight.status),
                        color: '#fff',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {flight.status}
                      </span>
                    </td>
                    <td style={{ padding: '16px', textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => navigate(`/admin/flights/edit/${flight.id}`)}
                          style={{
                            padding: '8px',
                            background: 'transparent',
                            border: '1px solid var(--border-light)',
                            cursor: 'pointer',
                            color: 'var(--text-secondary)'
                          }}
                          title="Modifier"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(flight.id, flight.flight_number)}
                          style={{
                            padding: '8px',
                            background: 'transparent',
                            border: '1px solid #c41e3a',
                            cursor: 'pointer',
                            color: '#c41e3a'
                          }}
                          title="Supprimer"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="body-small" style={{ marginTop: '16px', color: 'var(--text-secondary)' }}>
          {filteredFlights.length} vol(s) {searchTerm && 'trouvé(s)'}
        </p>
      </div>
    </AdminLayout>
  );
};

export default ManageFlights;